import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function PaintersNoordhoekPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isScrolled, setIsScrolled] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Noordhoek Painters | Professional Painting Services | paintersnoordhoek.co.za',
    description: 'Expert painting services in Noordhoek with 19+ years experience. Residential, commercial painting, interior and exterior. Free quotes, quality workmanship, competitive rates. Serving Noordhoek and surrounding coastal areas.',
    keywords: 'painters Noordhoek, painting services Noordhoek, house painters Noordhoek, commercial painters Noordhoek, residential painting Noordhoek',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Noordhoek',
          description: 'Professional painting services in Noordhoek with over 19 years of experience',
          address: 'Noordhoek, Western Cape',
          priceRange: '$$',
          serviceArea: ['Noordhoek', 'Kommetjie', 'Scarborough', 'Sun Valley', 'Fish Hoek']
        }),
        generateServiceSchema({
          name: 'Premium Coastal Painting',
          description: 'Residential and commercial painting services for coastal properties',
          provider: 'Painters Noordhoek',
          areaServed: ['Noordhoek', 'Kommetjie', 'Southern Peninsula'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Waterproofing']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Navigation */}
      <nav className="bg-black text-white py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-paint-brush-line text-2xl text-[#e9f638]"></i>
              <span className="text-xl font-bold">Cape Town Painters</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">About</Link>
              <Link to="/contact" className="hover:text-[#e9f638] transition-colors whitespace-nowrap">Contact</Link>
              <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get a Quote</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/0dfc9d0f7d721348e42720f5818bc835.jpg"
            alt="Painters Noordhoek - Professional Painting Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 w-full">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Painters Noordhoek</h1>
          <p className="text-xl md:text-2xl mb-6">Premium Painting Services in Noordhoek & Coastal Areas</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="http://paintersnoordhoek.co.za" target="_blank" rel="noopener noreferrer" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Visit Website</a>
            <Link to="/get-a-quote" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
          </div>
        </div>
      </div>

      {/* Quick Info Bar */}
      <div className="bg-[#e9f638] py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-black">19+</div>
              <div className="text-black font-medium">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">3800+</div>
              <div className="text-black font-medium">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">98%</div>
              <div className="text-black font-medium">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-black">Local</div>
              <div className="text-black font-medium">Noordhoek Specialists</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b sticky top-[72px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 border-b-2 transition-colors whitespace-nowrap font-medium ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-600 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div className="container mx-auto px-4 py-12">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">About Painters Noordhoek</h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Noordhoek</strong> has been the trusted name in professional painting services for Noordhoek and surrounding coastal communities for over 19 years. Nestled between the majestic Chapman's Peak and the pristine Noordhoek Beach, our company understands the unique challenges and opportunities of painting in this spectacular coastal environment. Our commitment to excellence, attention to detail, and deep understanding of coastal painting requirements has made us the preferred choice for homeowners and businesses throughout the area.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Story and Local Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                Founded in 2005, <strong>Painters Noordhoek</strong> was established by local residents who recognized the need for specialized painting services that could withstand the unique coastal conditions of the Noordhoek valley. Over the past 19 years, we have completed over 3,800 painting projects throughout Noordhoek, Kommetjie, Scarborough, Sun Valley, Fish Hoek, and surrounding areas. Our extensive experience includes working on everything from charming beach cottages and luxury coastal estates to commercial properties and equestrian facilities that characterize this beautiful region.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                The Noordhoek area presents distinctive painting challenges due to its exposed coastal location, strong southeasterly winds, salt spray from the Atlantic Ocean, intense sun exposure, and the unique microclimate created by the surrounding mountains. Our team has developed specialized expertise in addressing these challenges, using premium marine-grade materials, proven application techniques, and comprehensive surface preparation methods that ensure long-lasting, beautiful results that protect your investment and enhance your property's value.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Comprehensive Painting Solutions</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Noordhoek</strong>, we offer a complete range of painting services designed to meet the diverse needs of our coastal community. Our residential painting services include interior painting, exterior painting, roof painting and coating, deck and patio painting, fence and gate painting, damp proofing and waterproofing, and color consultation services. We work closely with homeowners to create beautiful, personalized spaces that reflect the relaxed coastal lifestyle while providing maximum protection against the elements.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                For commercial clients, we provide professional painting solutions that enhance property appeal, maintain professional appearances, and protect valuable assets. Our commercial services include retail space painting, restaurant and café painting, office painting, guesthouse and B&B painting, equestrian facility painting, and building exterior painting. We understand the importance of maintaining business operations and work efficiently to minimize disruption while delivering exceptional results that enhance your brand image and create positive impressions on customers and guests.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Coastal Protection Expertise</h3>
              <p className="text-lg leading-relaxed mb-6">
                One of our core specializations is protecting properties from the harsh coastal environment that characterizes the Noordhoek area. <strong>Painters Noordhoek</strong> uses premium marine-grade paints and protective coatings specifically formulated to resist salt corrosion, UV degradation, moisture penetration, wind-driven rain, and sand abrasion. Our comprehensive surface preparation process addresses existing damage, treats rust and corrosion on metal surfaces, seals cracks and gaps, removes salt deposits and contaminants, and applies appropriate primers before finishing coats.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We pay particular attention to areas most vulnerable to coastal damage, including exterior woodwork and trim, metal railings and fixtures, roof surfaces and flashings, decks, patios, and balconies, window and door frames, and garage doors. Our protective coating systems are designed to provide maximum durability while maintaining the natural beauty and character of your property. We also offer specialized waterproofing services to protect against water infiltration, which is essential given the high rainfall and strong winds experienced in the Noordhoek valley.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Premium Materials for Coastal Environments</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Noordhoek</strong>, we believe that exceptional results require exceptional materials. We partner with leading paint manufacturers to source premium products specifically designed for coastal and marine applications. Our product selection includes high-performance exterior paints with superior UV resistance, marine-grade primers and sealers, specialized rust-inhibiting coatings for metal surfaces, advanced waterproofing membranes and sealants, breathable masonry paints for damp-prone areas, and eco-friendly low-VOC options for interior applications.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We take the time to educate our clients about different product options and help them select the best solutions for their specific needs, budget, and environmental exposure. Our expertise in coastal painting means we can recommend products that will perform optimally in Noordhoek's unique climate, providing superior protection and longevity compared to standard painting products. We also offer color consultation services to help you choose colors that complement the natural coastal environment and enhance your property's aesthetic appeal.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Our Systematic Process</h3>
              <p className="text-lg leading-relaxed mb-6">
                Every project at <strong>Painters Noordhoek</strong> follows a proven process designed to ensure exceptional results and complete customer satisfaction. We begin with a comprehensive consultation and site assessment where we evaluate surface conditions, identify any damage or deterioration, assess environmental exposure factors, discuss your vision and requirements, and provide expert recommendations. This thorough evaluation allows us to develop an accurate, detailed quote and realistic timeline for your project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Our preparation phase is extensive and meticulous, including high-pressure washing to remove salt deposits, dirt, and contaminants, scraping and sanding to remove loose or failing paint, repairing damaged wood, stucco, or other surfaces, treating rust and corrosion on metal surfaces, filling cracks, gaps, and imperfections, applying specialized primers appropriate for the substrate and conditions, and protecting surrounding areas, landscaping, and fixtures. We never compromise on preparation because we know it's the foundation of a successful, long-lasting painting project.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                During the painting phase, our skilled craftsmen apply coatings using proven techniques that ensure even coverage, proper film thickness for maximum protection, clean, precise lines and edges, and professional finishes throughout. We carefully monitor weather conditions and adjust our schedule as needed to ensure optimal application conditions. Our attention to detail extends to every aspect of the work, from cutting in around trim and fixtures to achieving smooth, consistent finishes that enhance your property's beauty and value.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                Upon completion, we conduct a thorough final inspection with you to ensure every detail meets your expectations. We perform any necessary touch-ups, remove all protective materials, clean the work area thoroughly, dispose of waste responsibly, and provide maintenance recommendations to help you protect your investment. We don't consider a project complete until you're completely satisfied with the results.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Service Areas Throughout the Peninsula</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Noordhoek</strong> proudly serves Noordhoek and the entire southern peninsula. Our comprehensive service area includes Noordhoek, Kommetjie, Scarborough, Sun Valley, Fish Hoek, Clovelly, Kalk Bay, St James, Muizenberg, Simons Town, Glencairn, Ocean View, Masiphumelele, and surrounding coastal communities. Our local presence means we can respond quickly to inquiries, provide prompt service, and offer ongoing support to our clients throughout the region.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Environmental Stewardship</h3>
              <p className="text-lg leading-relaxed mb-6">
                As a company operating in one of the most beautiful and environmentally sensitive areas of the Cape Peninsula, <strong>Painters Noordhoek</strong> takes environmental responsibility very seriously. We are committed to sustainable painting practices that protect the natural environment and minimize our ecological footprint. Our environmental initiatives include using eco-friendly paints with low or zero VOC content, proper disposal of paint waste and hazardous materials, protecting sensitive coastal and wetland areas during projects, minimizing water usage and preventing runoff contamination, recycling paint containers and materials whenever possible, and supporting local environmental conservation efforts.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Customer Satisfaction Guarantee</h3>
              <p className="text-lg leading-relaxed mb-6">
                At <strong>Painters Noordhoek</strong>, your complete satisfaction is our ultimate goal. We stand behind the quality of our work with comprehensive warranties and guarantees. Our 98% customer satisfaction rating reflects our commitment to excellence and our dedication to exceeding client expectations on every project. We maintain open communication throughout the project, keeping you informed of progress, addressing any concerns promptly, and ensuring the final result meets or exceeds your expectations.
              </p>

              <p className="text-lg leading-relaxed mb-6">
                We understand that inviting contractors into your home or business requires trust. That's why we maintain the highest standards of professionalism, reliability, and integrity in all our interactions. Our team members are courteous, respectful, and committed to providing a positive experience from initial consultation through project completion. We treat every property as if it were our own, taking care to protect your belongings, respect your privacy, and maintain a clean, safe work environment throughout the project.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Competitive Pricing and Exceptional Value</h3>
              <p className="text-lg leading-relaxed mb-6">
                <strong>Painters Noordhoek</strong> offers competitive pricing without compromising on quality. We provide detailed, transparent quotes that clearly outline all costs, materials, and services included, so you know exactly what to expect. There are no hidden fees or surprise charges. Our pricing reflects the quality of materials we use, the expertise of our team, the comprehensive preparation we perform, and the professional service we provide. We believe in delivering exceptional value by combining quality workmanship, premium materials, and outstanding customer service at fair, competitive rates.
              </p>

              <h3 className="text-2xl font-bold mt-8 mb-4">Get Started with Your Painting Project</h3>
              <p className="text-lg leading-relaxed mb-6">
                Ready to protect and beautify your Noordhoek property with professional painting services? Contact <strong>Painters Noordhoek</strong> today for a free, no-obligation quote. Our friendly team is ready to discuss your project, answer your questions, and provide expert guidance on all your painting needs. With over 19 years of coastal painting experience, thousands of satisfied customers, and a commitment to excellence, we're the painting partner you can trust to deliver outstanding results that enhance your property's beauty and withstand the test of time and the coastal elements.
              </p>

              <div className="bg-[#e9f638] p-8 rounded-lg mt-8">
                <h4 className="text-2xl font-bold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <p className="flex items-center text-lg">
                    <i className="ri-global-line text-2xl mr-3"></i>
                    <a href="http://paintersnoordhoek.co.za" target="_blank" rel="noopener noreferrer" className="hover:underline font-medium">paintersnoordhoek.co.za</a>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-map-pin-line text-2xl mr-3"></i>
                    <span className="font-medium">Serving Noordhoek & Southern Peninsula</span>
                  </p>
                  <p className="flex items-center text-lg">
                    <i className="ri-time-line text-2xl mr-3"></i>
                    <span className="font-medium">Monday - Saturday: 7:30 AM - 5:30 PM</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Painting Services</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=coastal%20home%20interior%20painting%20Noordhoek%20beach%20house%20fresh%20painted%20walls%20ocean%20view%20elegant%20furniture%20natural%20lighting%20professional%20finish%20simple%20background&width=400&height=300&seq=service-residential-nh&orientation=landscape"
                    alt="Residential Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Residential Painting</h3>
                  <p className="text-gray-700 mb-4">Complete interior and exterior painting for coastal homes, using premium marine-grade products for lasting protection.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Interior wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Exterior house painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Coastal protection coating</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Deck and patio painting</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=commercial%20building%20painting%20Noordhoek%20business%20property%20professional%20painters%20working%20clean%20organized%20simple%20background&width=400&height=300&seq=service-commercial-nh&orientation=landscape"
                    alt="Commercial Painting Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Commercial Painting</h3>
                  <p className="text-gray-700 mb-4">Professional painting for businesses and commercial properties with minimal disruption to operations.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Retail space painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Restaurant painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Guesthouse painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building exteriors</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=waterproofing%20roof%20coating%20protection%20coastal%20building%20Noordhoek%20marine%20environment%20professional%20application%20simple%20background&width=400&height=300&seq=service-waterproof-nh&orientation=landscape"
                    alt="Waterproofing Services"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Waterproofing</h3>
                  <p className="text-gray-700 mb-4">Specialized waterproofing services to protect your property from coastal moisture and weather.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof waterproofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Wall damp proofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Deck waterproofing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Basement sealing</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=exterior%20building%20painting%20coastal%20property%20Noordhoek%20marine%20grade%20paint%20professional%20finish%20mountain%20view%20simple%20background&width=400&height=300&seq=service-exterior-nh&orientation=landscape"
                    alt="Exterior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Exterior Painting</h3>
                  <p className="text-gray-700 mb-4">Marine-grade exterior painting designed to withstand harsh coastal conditions and salt spray.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Building facades</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Roof painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Fence and gate painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Rust treatment</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=interior%20room%20painting%20elegant%20coastal%20home%20Noordhoek%20fresh%20colors%20professional%20finish%20comfortable%20atmosphere%20simple%20background&width=400&height=300&seq=service-interior-nh&orientation=landscape"
                    alt="Interior Painting"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Interior Painting</h3>
                  <p className="text-gray-700 mb-4">Beautiful interior painting services using premium paints for lasting beauty and durability.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Wall painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Ceiling painting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trim and molding</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Feature walls</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="w-full h-48">
                  <img 
                    src="https://readdy.ai/api/search-image?query=color%20consultation%20paint%20samples%20swatches%20coastal%20interior%20design%20Noordhoek%20planning%20professional%20advice%20simple%20background&width=400&height=300&seq=service-consultation-nh&orientation=landscape"
                    alt="Color Consultation"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-3">Color Consultation</h3>
                  <p className="text-gray-700 mb-4">Expert color consultation to help you choose perfect colors that complement coastal living.</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Color scheme design</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Sample testing</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Coastal color palettes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-[#e9f638] mr-2 mt-1"></i>
                      <span>Trend guidance</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Work Gallery</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20home%20interior%20Noordhoek%20beach%20view%20fresh%20walls%20elegant%20furniture%20natural%20lighting%20simple%20background&width=400&height=500&seq=gallery-nh-1&orientation=portrait"
                  alt="Coastal Home Interior Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=beautiful%20house%20exterior%20freshly%20painted%20Noordhoek%20coastal%20property%20mountain%20view%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-nh-2&orientation=portrait"
                  alt="Exterior House Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=commercial%20building%20painted%20Noordhoek%20business%20property%20professional%20finish%20coastal%20setting%20simple%20background&width=400&height=500&seq=gallery-nh-3&orientation=portrait"
                  alt="Commercial Property Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=elegant%20bedroom%20interior%20painting%20coastal%20home%20Noordhoek%20soft%20colors%20professional%20finish%20comfortable%20atmosphere%20simple%20background&width=400&height=500&seq=gallery-nh-4&orientation=portrait"
                  alt="Bedroom Painting Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=waterproofed%20roof%20coating%20coastal%20building%20Noordhoek%20marine%20protection%20professional%20application%20simple%20background&width=400&height=500&seq=gallery-nh-5&orientation=portrait"
                  alt="Roof Waterproofing Project"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="w-full h-80 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=painted%20deck%20patio%20coastal%20home%20Noordhoek%20beach%20view%20marine%20grade%20coating%20professional%20finish%20simple%20background&width=400&height=500&seq=gallery-nh-6&orientation=portrait"
                  alt="Deck and Patio Painting"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Client Reviews</h2>
            
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters Noordhoek did an outstanding job on our beach house. They understood the challenges of painting in a coastal environment and used the right products to ensure lasting protection. The team was professional, reliable, and the results are beautiful!"</p>
                <p className="font-semibold">- Emma S., Noordhoek</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We hired Painters Noordhoek for our guesthouse in Kommetjie. They worked efficiently, maintained a clean work area, and delivered exceptional results. Their knowledge of coastal painting is impressive. Highly recommend their services!"</p>
                <p className="font-semibold">- John M., Kommetjie</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Excellent service from Painters Noordhoek! They painted the exterior of our home in Sun Valley and the transformation is amazing. The marine-grade paint they used has held up perfectly. Very professional and trustworthy team!"</p>
                <p className="font-semibold">- Sarah L., Sun Valley</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"Painters Noordhoek provided outstanding service for our waterproofing and painting project. Their expertise in coastal protection is evident in the quality of their work. The team was friendly, punctual, and delivered exactly what they promised!"</p>
                <p className="font-semibold">- Michael T., Scarborough</p>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-[#e9f638] text-xl">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="ml-2 text-gray-600 font-medium">5.0</span>
                </div>
                <p className="text-gray-700 mb-4 italic">"We're thrilled with the interior painting work done by Painters Noordhoek. Their color consultation service was invaluable, and the painters were meticulous in their work. Our home looks stunning! Best painting company in the area!"</p>
                <p className="font-semibold">- Rachel P., Noordhoek</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-black text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Coastal Property?</h2>
          <p className="text-xl mb-8">Get a free quote from Painters Noordhoek today</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/get-a-quote" className="bg-[#e9f638] text-black px-8 py-3 rounded-lg hover:bg-[#d4e030] transition-colors whitespace-nowrap font-semibold">Get Free Quote</Link>
            <a href="http://paintersnoordhoek.co.za" target="_blank" rel="noopener noreferrer" className="bg-white text-black px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap font-semibold">Visit Website</a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#e9f638] text-black py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Cape Town Painters</h3>
              <p className="text-sm">Your trusted directory for professional painting services across South Africa.</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:underline">Home</Link></li>
                <li><Link to="/about" className="hover:underline">About</Link></li>
                <li><Link to="/contact" className="hover:underline">Contact</Link></li>
                <li><Link to="/get-a-quote" className="hover:underline">Get a Quote</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Residential Painting</li>
                <li>Commercial Painting</li>
                <li>Waterproofing</li>
                <li>Coastal Protection</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Cape Town, South Africa
                </li>
                <li className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  info@painters-cape-town.co.za
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-black/20 pt-8 text-center text-sm">
            <p>&copy; 2024 Cape Town Painters Directory. All rights reserved.</p>
            <div className="mt-2 space-x-4">
              <a href="https://leaderr.co/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">Powered by leaderr</a>
              <span>|</span>
              <a href="https://seostudio.co.za/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700 transition-colors">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}